/**
 * Centralised logger singleton
 *
 * Delegates to the active TelemetryBackend (App Insights or AWS RUM).
 * When no backend is set (e.g. missing connection string), all calls silently no-op.
 *
 * This is a plain module singleton (NOT a React hook), so it works everywhere:
 * - api/client.ts (non-React)
 * - ErrorBoundary (class component)
 * - Auth providers and standalone functions
 *
 * Usage:
 *   import { logger } from "@/telemetry";
 *   logger.error("Failed to save", error, { partnerId: "123" });
 */

import type { TelemetryProperties, TelemetryUser, DependencyData } from "./types";
import type { TelemetryBackend } from "./backends/types";

import { LogLevel } from "./types";

/** The active telemetry backend (set during app initialisation) */
let backend: TelemetryBackend | null = null;

function parseLogLevel(): LogLevel {
  const env = (import.meta.env.VITE_LOG_LEVEL as string | undefined)?.toUpperCase();

  if (env && env in LogLevel) {
    return LogLevel[env as keyof typeof LogLevel];
  }

  return import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO;
}

const MIN_LEVEL: LogLevel = parseLogLevel();

/** Called once to wire up the backend. Safe to call multiple times (first wins). */
export function setBackend(b: TelemetryBackend): void {
  if (!backend) {
    backend = b;
  }
}

/** For testing: reset the backend */
export function _resetBackend(): void {
  backend = null;
}

export const logger = {
  /** Log a trace message at a specific level */
  trace(level: LogLevel, message: string, properties?: TelemetryProperties): void {
    if (level < MIN_LEVEL) return;

    backend?.trace(level, message, properties);
  },

  /** Debug-level trace */
  debug(message: string, properties?: TelemetryProperties): void {
    this.trace(LogLevel.DEBUG, message, properties);
  },

  /** Info-level trace */
  info(message: string, properties?: TelemetryProperties): void {
    this.trace(LogLevel.INFO, message, properties);
  },

  /** Warning-level trace */
  warn(message: string, properties?: TelemetryProperties): void {
    this.trace(LogLevel.WARN, message, properties);
  },

  /** Error-level trace with optional Error object for exception tracking */
  error(message: string, error?: unknown, properties?: TelemetryProperties): void {
    this.trace(LogLevel.ERROR, message, properties);

    if (error instanceof Error) {
      this.trackException(error, properties);
    }
  },

  /** Track an exception (sent to the backend's exception tracking) */
  trackException(error: Error, properties?: TelemetryProperties, componentStack?: string): void {
    backend?.trackException(error, properties, componentStack);
  },

  /** Track a custom event */
  trackEvent(name: string, properties?: TelemetryProperties): void {
    backend?.trackEvent(name, properties);
  },

  /** Track an API dependency call */
  trackDependency(data: DependencyData): void {
    backend?.trackDependency(data);
  },

  /** Track a page view manually (for non-router navigations) */
  trackPageView(name: string, uri?: string): void {
    backend?.trackPageView(name, uri);
  },

  /** Set authenticated user context (call after login) */
  setUser(user: TelemetryUser): void {
    backend?.setUser(user);
  },

  /** Clear user context (call on logout) */
  clearUser(): void {
    backend?.clearUser();
  },

  /** Flush pending telemetry (call before page unload) */
  flush(): void {
    backend?.flush();
  },
};
